// Upload Service v2 engine: multipart upload of one file to a session created
// by evt.live (/api/uploads/v2), then completion. See README.md.
//
// Platform-agnostic on purpose: no React or React Native imports (only t()
// from @/localization, for the messages people read). Everything that touches
// the file or the network for the parts comes in through an adapter (web:
// adapter.web.js):
//   readPart(file, start, end)              → body for the part PUT
//   putPart(url, body, onProgress, signal)  → resolves on 2xx; rejects with
//       { status, code: "http" | "network" | "timeout" | "aborted", body? }
//       onProgress(loadedBytes) reports bytes sent for this part so far
//   isOnline?() and waitForOnline?(signal)  → offline waits don't use up tries
// and the evt.live calls through an api object (api.js):
//   signParts(id, uploadToken, partNumbers) → { urls: { "1": "https://…" }, expiresAt }
//   complete(id, uploadToken)               → { status }

import { t } from "@/localization";

export const UPLOAD_V2_DEFAULTS = Object.freeze({
  // Parts in flight adapt to the link: start at 3, drop to 2 once a part
  // takes longer than slowPartMs (so slow links checkpoint sooner), and go up
  // to 4 when the measured speed would still finish parts well within it.
  // A fixed `concurrency` (number) turns this off.
  initialConcurrency: 3,
  minConcurrency: 2,
  maxConcurrency: 4,
  slowPartMs: 60000,
  // Part URLs are requested in batches ahead of need (the API takes ≤100).
  signBatchSize: 50,
  // Tries per part (and for the completion call) before the upload stops.
  maxTries: 6,
  backoffBaseMs: 1000,
  backoffMaxMs: 30000,
  // A URL this close to expiry is signed again before it's used.
  urlExpiryMarginMs: 2 * 60 * 1000,
  // Rounds of "complete → re-upload the parts evt.live says are missing".
  completeRounds: 3,
  pollIntervalMs: 10000,
});

// The API signs part URLs for 6 h unless asked otherwise; used when a response
// has no readable expiresAt.
const DEFAULT_URL_TTL_MS = 6 * 60 * 60 * 1000;

export const MAX_UPLOAD_BYTES = 200 * 1024 ** 3;

export const UPLOAD_INTERRUPTED_MESSAGE = t(
  "The upload was interrupted. Check your internet connection and try again."
);
export const UPLOAD_EXPIRED_MESSAGE = t(
  "This upload has expired. Please choose the file again."
);
export const UPLOAD_MISMATCH_MESSAGE = t(
  "This upload doesn't match the file. Please choose the file again."
);
export const FINISH_FAILED_MESSAGE = t(
  "Could not finish the upload. Please try again."
);

export class UploadV2Error extends Error {
  constructor(
    message,
    {
      status = 0,
      code = "error",
      retryable = false,
      stage = null,
      missingParts = null,
      existing = null,
      cause = null,
    } = {}
  ) {
    super(message);
    this.name = "UploadV2Error";
    this.status = status;
    this.code = code;
    this.retryable = retryable;
    this.stage = stage;
    if (missingParts) this.missingParts = missingParts;
    if (existing) this.existing = existing;
    if (cause) this.cause = cause;
  }
}

export const isAbortError = (error) =>
  error?.code === "aborted" || error?.name === "AbortError";

export const abortError = (signal) =>
  new UploadV2Error("Upload stopped.", {
    code: "aborted",
    cause: signal?.reason ?? null,
  });

const throwIfAborted = (signal) => {
  if (signal?.aborted) throw abortError(signal);
};

// Settles with `promise`, or rejects as soon as `signal` aborts, so a pause
// or cancel never waits on an adapter (or a request) that ignores the signal.
const untilAborted = (promise, signal) => {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError(signal));
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError(signal));
    signal.addEventListener("abort", onAbort, { once: true });
    Promise.resolve(promise).then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      }
    );
  });
};

export function sleep(
  ms,
  signal,
  setTimer = setTimeout,
  clearTimer = clearTimeout
) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError(signal));
      return;
    }
    let timer = null;
    const onAbort = () => {
      clearTimer(timer);
      reject(abortError(signal));
    };
    timer = setTimer(() => {
      signal?.removeEventListener?.("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener?.("abort", onAbort, { once: true });
  });
}

// Exponential backoff with jitter: half of the window is fixed, half random, so
// retries from parallel parts spread out but never fire immediately.
export function backoffDelay(
  attempt,
  { backoffBaseMs = 1000, backoffMaxMs = 30000, random = Math.random } = {}
) {
  const exponent = Math.max(0, attempt - 1);
  const window = Math.min(backoffMaxMs, backoffBaseMs * 2 ** exponent);
  return Math.round(window / 2 + random() * (window / 2));
}

// Accepts ISO strings, epoch seconds or epoch milliseconds.
export function parseExpiresAt(value, fallback = null) {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === "string" && value.trim()) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return parseExpiresAt(numeric, fallback);
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

// Byte ranges for parts 1..partCount, exactly as evt.live checks them on
// complete: every part but the last is partSize, and they add up to size.
export function planParts(size, partSize, partCount) {
  const total = Number(size);
  const each = Number(partSize);
  const count = Number(partCount);
  if (
    !Number.isSafeInteger(total) ||
    total < 1 ||
    !Number.isSafeInteger(each) ||
    each < 1 ||
    !Number.isSafeInteger(count) ||
    count < 1 ||
    Math.ceil(total / each) !== count
  ) {
    throw new UploadV2Error(UPLOAD_MISMATCH_MESSAGE, { code: "plan" });
  }
  const parts = new Array(count);
  for (let index = 0; index < count; index += 1) {
    const start = index * each;
    const end = Math.min(start + each, total);
    parts[index] = { partNumber: index + 1, start, end, size: end - start };
  }
  return parts;
}

// Part numbers evt.live already holds (resume). Accepts plain numbers or
// ListParts-style objects; a part reported at the wrong size is uploaded again.
export function uploadedPartNumbers(uploadedParts, parts) {
  const done = new Set();
  if (!Array.isArray(uploadedParts) || !Array.isArray(parts)) return done;
  for (const entry of uploadedParts) {
    const isObject = entry !== null && typeof entry === "object";
    const number = Number(
      isObject ? entry.partNumber ?? entry.PartNumber ?? entry.number : entry
    );
    const part = Number.isInteger(number) ? parts[number - 1] : undefined;
    if (!part) continue;
    const reportedSize = isObject ? Number(entry.size ?? entry.Size) : NaN;
    if (Number.isFinite(reportedSize) && reportedSize !== part.size) continue;
    done.add(number);
  }
  return done;
}

export const normalizeStatus = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isTransientStatus = (status) =>
  status === 0 || status === 408 || status === 429 || status >= 500;

// A presigned part URL the storage refused: expired or signature trouble.
const urlRejected = (error) =>
  error?.status === 401 ||
  error?.status === 403 ||
  (error?.status === 400 && /expired/i.test(String(error?.body || "")));

// The upload token was refused (expired after 7 days, or tampered with).
// Without evt.live's code, a 401 on a session endpoint means the same.
export const isTokenError = (error) =>
  error?.code === "token_expired" ||
  error?.code === "token_invalid" ||
  (error?.code === "http" &&
    error?.status === 401 &&
    error?.stage !== "create" &&
    error?.stage !== "list");

// code "token": the caller can get a fresh token by creating the session again
// (evt.live resumes the same B2 upload).
const tokenError = (error, stage) =>
  new UploadV2Error(UPLOAD_EXPIRED_MESSAGE, {
    status: error?.status || 401,
    code: "token",
    stage,
    cause: error,
  });

const sessionGoneError = (error, stage = error?.stage || null) =>
  new UploadV2Error(UPLOAD_EXPIRED_MESSAGE, {
    status: error?.status || 0,
    code: "session_gone",
    stage,
    cause: error,
  });

const isOffline = (adapter) =>
  Boolean(adapter?.isOnline && adapter.waitForOnline && !adapter.isOnline());

// Waits for the connection without using up a try. Returns true if it waited.
const waitWhileOffline = async (adapter, signal, onNetworkWait) => {
  if (!isOffline(adapter)) return false;
  onNetworkWait?.(true);
  try {
    await untilAborted(adapter.waitForOnline(signal), signal);
  } finally {
    onNetworkWait?.(false);
  }
  return true;
};

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

// How many parts to keep in flight: fixed (`concurrency`), or adapting to the
// link between min and max. Each finished part gives a link-speed sample
// (bytes × parts in flight ÷ time); the limit is the most parts that would
// each still finish within slowPartMs (75% of it to go up). A part running
// past slowPartMs drops it to the minimum straight away.
export function createConcurrencyControl({
  concurrency,
  initialConcurrency = UPLOAD_V2_DEFAULTS.initialConcurrency,
  minConcurrency = UPLOAD_V2_DEFAULTS.minConcurrency,
  maxConcurrency = UPLOAD_V2_DEFAULTS.maxConcurrency,
  slowPartMs = UPLOAD_V2_DEFAULTS.slowPartMs,
  partSize,
  onChange,
}) {
  const fixed = Number.isInteger(concurrency) && concurrency > 0;
  const max = fixed ? concurrency : Math.max(1, maxConcurrency);
  const min = fixed ? concurrency : clamp(minConcurrency, 1, max);
  let limit = fixed ? concurrency : clamp(initialConcurrency, min, max);
  const speeds = [];

  const set = (next) => {
    const value = clamp(next, min, max);
    if (value === limit) return;
    limit = value;
    onChange?.(limit);
  };

  return {
    get limit() {
      return limit;
    },
    get max() {
      return max;
    },
    // A part has been in flight for elapsedMs.
    running(elapsedMs) {
      if (!fixed && elapsedMs > slowPartMs) set(min);
    },
    // A part of `bytes` finished after durationMs with `parallel` in flight.
    finished(bytes, durationMs, parallel) {
      if (fixed || !(durationMs > 0) || !(bytes > 0)) return;
      if (durationMs > slowPartMs) {
        set(min);
        return;
      }
      speeds.push((bytes * Math.max(1, parallel) * 1000) / durationMs);
      if (speeds.length > 4) speeds.shift();
      const linkBytesPerSecond = median(speeds);
      let best = min;
      for (let count = max; count > min; count -= 1) {
        const expectedMs = ((partSize * count) / linkBytesPerSecond) * 1000;
        if (expectedMs <= (count > limit ? slowPartMs * 0.75 : slowPartMs)) {
          best = count;
          break;
        }
      }
      set(best);
    },
  };
}

// Uploads every part of `parts` that isn't in `done` (a Set it updates),
// several at a time (see createConcurrencyControl). Resolves when all parts are acknowledged;
// rejects with an UploadV2Error: code "aborted" (signal), "session_gone",
// "session_state" (evt.live says the session isn't uploading any more),
// "exhausted" (a part failed maxTries times; retryable) or "file".
export async function uploadParts({
  file,
  parts,
  done,
  session,
  api,
  adapter,
  signal,
  concurrency,
  initialConcurrency,
  minConcurrency,
  maxConcurrency,
  slowPartMs,
  signBatchSize = UPLOAD_V2_DEFAULTS.signBatchSize,
  maxTries = UPLOAD_V2_DEFAULTS.maxTries,
  backoffBaseMs = UPLOAD_V2_DEFAULTS.backoffBaseMs,
  backoffMaxMs = UPLOAD_V2_DEFAULTS.backoffMaxMs,
  urlExpiryMarginMs = UPLOAD_V2_DEFAULTS.urlExpiryMarginMs,
  random = Math.random,
  now = () => Date.now(),
  sleep: wait = sleep,
  onProgress,
  onPartDone,
  onNetworkWait,
  onConcurrency,
}) {
  throwIfAborted(signal);
  const batchLimit = Math.max(1, Math.min(100, signBatchSize));
  const lanes = createConcurrencyControl({
    concurrency,
    initialConcurrency,
    minConcurrency,
    maxConcurrency,
    slowPartMs,
    partSize: parts[0]?.size || 1,
    onChange: (limit) => {
      onConcurrency?.(limit);
      wake(); // more lanes: let waiting workers start
    },
  });
  // Workers waiting for a free lane.
  let running = 0;
  const waiting = [];
  function wake() {
    waiting.splice(0).forEach((resume) => resume());
  }
  const pending = parts
    .filter((part) => !done.has(part.partNumber))
    .map((part) => part.partNumber);
  // Parts a worker has taken and not finished yet.
  const claimed = new Set();
  const urls = new Map();
  const inflight = new Map();
  let ackedBytes = parts.reduce(
    (sum, part) => (done.has(part.partNumber) ? sum + part.size : sum),
    0
  );
  let signing = null;
  let failure = null;

  // Stops the other workers when one hits a fatal error; follows `signal`.
  const controller = new AbortController();
  const runSignal = controller.signal;
  const onOuterAbort = () => controller.abort(signal.reason);
  signal?.addEventListener?.("abort", onOuterAbort, { once: true });
  runSignal.addEventListener("abort", wake, { once: true });

  const report = () => {
    let inflightBytes = 0;
    inflight.forEach((bytes) => {
      inflightBytes += bytes;
    });
    onProgress?.({ ackedBytes, inflightBytes });
  };

  const validUrl = (partNumber, marginMs = urlExpiryMarginMs) => {
    const cached = urls.get(partNumber);
    return cached && cached.expiresAtMs - now() > marginMs ? cached.url : null;
  };

  // One request for `first` (if any), the other taken parts and then the
  // next queued parts without a usable URL, up to the batch limit.
  const signBatch = async (first) => {
    const batch = first == null ? [] : [first];
    const candidates = [...[...claimed].sort((a, b) => a - b), ...pending];
    for (const partNumber of candidates) {
      if (batch.length >= batchLimit) break;
      if (!batch.includes(partNumber) && !validUrl(partNumber)) {
        batch.push(partNumber);
      }
    }
    if (!batch.length) return;
    const requestedAt = now();
    let result;
    try {
      result = await api.signParts(session.id, session.uploadToken, batch);
    } catch (error) {
      if (error && typeof error === "object") error.stage = "sign";
      throw error;
    }
    const expiresAtMs = parseExpiresAt(
      result?.expiresAt,
      requestedAt + DEFAULT_URL_TTL_MS
    );
    Object.entries(result?.urls || {}).forEach(([key, url]) => {
      const partNumber = Number(key);
      if (Number.isInteger(partNumber) && typeof url === "string" && url) {
        urls.set(partNumber, { url, expiresAtMs });
      }
    });
  };

  const startSigning = (first) => {
    const request = signBatch(first).finally(() => {
      if (signing === request) signing = null;
    });
    signing = request;
    return request;
  };

  const ensureUrl = async (partNumber) => {
    for (;;) {
      throwIfAborted(runSignal);
      const url = validUrl(partNumber);
      if (url) return url;
      if (signing) {
        // Another worker (or the prefetch) is signing: wait, then look again.
        await untilAborted(
          signing.catch(() => {}),
          runSignal
        );
        continue;
      }
      await untilAborted(startSigning(partNumber), runSignal);
      // Just signed: use it unless it has already expired.
      const signed = validUrl(partNumber, 0);
      if (signed) return signed;
      throw new UploadV2Error(UPLOAD_INTERRUPTED_MESSAGE, {
        code: "sign",
        stage: "sign",
        retryable: true,
      });
    }
  };

  // Keep URLs ready for the parts the workers pick up next.
  const prefetch = () => {
    if (signing || runSignal.aborted) return;
    const upcoming = pending.slice(0, Math.max(2, lanes.max * 2));
    if (upcoming.some((partNumber) => !validUrl(partNumber))) {
      startSigning(null).catch(() => {});
    }
  };

  const uploadOne = async (partNumber) => {
    const part = parts[partNumber - 1];
    let tries = 0;
    let startedAt = now();
    let parallel = running;
    for (;;) {
      throwIfAborted(runSignal);
      if (await waitWhileOffline(adapter, runSignal, onNetworkWait)) continue;

      let error = null;
      try {
        const url = await ensureUrl(partNumber);
        prefetch();
        let body;
        try {
          body = await untilAborted(
            adapter.readPart(file, part.start, part.end),
            runSignal
          );
        } catch (readError) {
          throwIfAborted(runSignal);
          throw new UploadV2Error(
            t("Could not read the video file. Please choose it again."),
            { code: "file", cause: readError }
          );
        }
        startedAt = now();
        parallel = running;
        await untilAborted(
          adapter.putPart(
            url,
            body,
            (loaded) => {
              if (runSignal.aborted) return;
              inflight.set(
                partNumber,
                Math.min(Math.max(0, Number(loaded) || 0), part.size)
              );
              lanes.running(now() - startedAt);
              report();
            },
            runSignal
          ),
          runSignal
        );
        lanes.finished(part.size, now() - startedAt, parallel);
        inflight.delete(partNumber);
        done.add(partNumber);
        ackedBytes += part.size;
        report();
        onPartDone?.(partNumber);
        return;
      } catch (caught) {
        inflight.delete(partNumber);
        report();
        throwIfAborted(runSignal);
        error = caught;
      }

      if (error?.code === "file") throw error;
      if (error?.stage === "sign" && error?.code !== "sign") {
        if (isTokenError(error)) throw tokenError(error, "sign");
        if (error.status === 409) {
          throw new UploadV2Error(error.message, {
            status: 409,
            code: "session_state",
            stage: "sign",
            cause: error,
          });
        }
        if (!isTransientStatus(error.status) && !error.retryable) {
          throw sessionGoneError(error);
        }
      } else if (error?.status === 404) {
        // NoSuchUpload: the multipart upload was aborted or expired.
        throw sessionGoneError(error);
      } else if (urlRejected(error)) {
        urls.delete(partNumber);
      }

      if (!error?.status && isOffline(adapter)) {
        continue; // went offline mid-part: wait for the connection, keep the try
      }

      tries += 1;
      if (tries >= maxTries) {
        throw new UploadV2Error(UPLOAD_INTERRUPTED_MESSAGE, {
          status: error?.status || 0,
          code: "exhausted",
          retryable: true,
          cause: error,
        });
      }
      await wait(
        backoffDelay(tries, { backoffBaseMs, backoffMaxMs, random }),
        runSignal
      );
    }
  };

  const worker = async () => {
    for (;;) {
      throwIfAborted(runSignal);
      if (!pending.length) return;
      if (running >= lanes.limit) {
        await new Promise((resume) => waiting.push(resume));
        continue;
      }
      const partNumber = pending.shift();
      running += 1;
      claimed.add(partNumber);
      try {
        await uploadOne(partNumber);
      } finally {
        claimed.delete(partNumber);
        running -= 1;
        wake();
      }
    }
  };

  report();
  const workerCount = Math.max(1, Math.min(lanes.max, pending.length));
  try {
    await Promise.all(
      Array.from({ length: pending.length ? workerCount : 0 }, () =>
        worker().catch((error) => {
          if (!failure) failure = error;
          controller.abort();
        })
      )
    );
  } finally {
    signal?.removeEventListener?.("abort", onOuterAbort);
  }
  throwIfAborted(signal);
  if (failure) throw failure;
}

// Asks evt.live to assemble the parts and queue the conversion. Retries
// transient failures; a 409 with missingParts is passed to the caller.
export async function completeUpload({
  session,
  api,
  adapter,
  signal,
  maxTries = UPLOAD_V2_DEFAULTS.maxTries,
  backoffBaseMs = UPLOAD_V2_DEFAULTS.backoffBaseMs,
  backoffMaxMs = UPLOAD_V2_DEFAULTS.backoffMaxMs,
  random = Math.random,
  sleep: wait = sleep,
  onNetworkWait,
}) {
  let tries = 0;
  for (;;) {
    throwIfAborted(signal);
    if (await waitWhileOffline(adapter, signal, onNetworkWait)) continue;
    let error;
    try {
      const result = await untilAborted(
        api.complete(session.id, session.uploadToken),
        signal
      );
      return { status: normalizeStatus(result?.status) || "queued" };
    } catch (caught) {
      throwIfAborted(signal);
      error = caught;
    }
    if (isTokenError(error)) throw tokenError(error, "complete");
    if (error?.code === "event_live") {
      // evt.live completes it once the stream ends (its cron does too).
      throw new UploadV2Error(error.message, {
        status: error.status,
        code: "event_live",
        stage: "complete",
        retryable: true,
        cause: error,
      });
    }
    if (error?.status === 409) {
      if (Array.isArray(error.missingParts) && error.missingParts.length) {
        throw error;
      }
      throw new UploadV2Error(error.message || FINISH_FAILED_MESSAGE, {
        status: 409,
        code: "session_state",
        stage: "complete",
        cause: error,
      });
    }
    if ([401, 403, 404, 410].includes(error?.status)) {
      throw sessionGoneError(error, "complete");
    }
    if (!error?.status && isOffline(adapter)) continue;
    if (!isTransientStatus(error?.status ?? 0) && !error?.retryable) {
      throw new UploadV2Error(error?.message || FINISH_FAILED_MESSAGE, {
        status: error?.status || 0,
        code: "complete",
        stage: "complete",
        cause: error,
      });
    }
    tries += 1;
    if (tries >= maxTries) {
      throw new UploadV2Error(FINISH_FAILED_MESSAGE, {
        status: error?.status || 0,
        code: "exhausted",
        stage: "complete",
        retryable: true,
        cause: error,
      });
    }
    await wait(
      backoffDelay(tries, { backoffBaseMs, backoffMaxMs, random }),
      signal
    );
  }
}

// Upload what's missing, then complete; if evt.live reports missing parts,
// upload those again (up to completeRounds). Resolves with { status } from
// complete: "queued" (or "converting"/"done" when it was already finished).
export async function runUpload(options) {
  const {
    parts,
    done,
    completeRounds = UPLOAD_V2_DEFAULTS.completeRounds,
    onPhase,
  } = options;
  for (let round = 1; ; round += 1) {
    onPhase?.("uploading");
    await uploadParts(options);
    onPhase?.("completing");
    try {
      return await completeUpload(options);
    } catch (error) {
      const missing = Array.isArray(error?.missingParts)
        ? error.missingParts
            .map(Number)
            .filter((n) => Number.isInteger(n) && parts[n - 1])
        : [];
      if (error?.status === 409 && missing.length && round < completeRounds) {
        missing.forEach((partNumber) => done.delete(partNumber));
        continue;
      }
      if (error?.status === 409 && error?.missingParts) {
        throw new UploadV2Error(error.message || FINISH_FAILED_MESSAGE, {
          status: 409,
          code: "incomplete",
          stage: "complete",
          cause: error,
        });
      }
      throw error;
    }
  }
}

// Polls GET status: once right away (or after one interval with
// start({ immediate: false })), then every intervalMs (a number, or a function
// returning the next interval) until onStatus returns "stop", onError returns
// false, or stop() is called. start() after stop() resumes polling.
export function createStatusWatcher({
  fetchStatus,
  onStatus,
  onError,
  intervalMs = UPLOAD_V2_DEFAULTS.pollIntervalMs,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
}) {
  let timer = null;
  let stopped = true;
  let finished = false;
  let inFlight = false;

  const schedule = () => {
    if (!stopped && !finished && !inFlight && !timer) {
      timer = setTimer(
        tick,
        typeof intervalMs === "function" ? intervalMs() : intervalMs
      );
    }
  };

  async function tick() {
    timer = null;
    if (stopped || finished) return;
    inFlight = true;
    try {
      const status = await fetchStatus();
      if (onStatus?.(status) === "stop") finished = true;
    } catch (error) {
      if (onError?.(error) === false) finished = true;
    } finally {
      inFlight = false;
    }
    schedule();
  }

  return {
    start({ immediate = true } = {}) {
      if (finished || !stopped) return;
      stopped = false;
      if (inFlight) return;
      if (immediate) tick();
      else schedule();
    },
    stop() {
      stopped = true;
      if (timer) clearTimer(timer);
      timer = null;
    },
    get active() {
      return !stopped && !finished;
    },
    get finished() {
      return finished;
    },
  };
}
